/**
 * Simple in-memory cache with TTL support
 * Used to reduce redundant API calls and improve perceived performance
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class ApiCache {
  private cache: Map<string, CacheEntry<unknown>> = new Map();
  private pendingRequests: Map<string, Promise<unknown>> = new Map();

  /**
   * Get cached data if it exists and hasn't expired
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key) as CacheEntry<T> | undefined;

    if (!entry) {
      return null;
    }

    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Set cached data with a TTL (time to live) in milliseconds
   */
  set<T>(key: string, data: T, ttl: number = 60000): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }

  /**
   * Remove a specific cache entry
   */
  invalidate(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Remove all cache entries matching a prefix
   */
  invalidatePrefix(prefix: string): void {
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Wrap an async function with caching
   * Prevents duplicate concurrent requests for the same key
   */
  async withCache<T>(
    key: string,
    fetcher: () => Promise<T>,
    ttl: number = 60000
  ): Promise<T> {
    // Check if we have cached data
    const cached = this.get<T>(key);
    if (cached !== null) {
      return cached;
    }

    // Check if there's already a pending request for this key
    const pending = this.pendingRequests.get(key) as Promise<T> | undefined;
    if (pending) {
      return pending;
    }

    // Create new request and store it
    const request = fetcher().then((data) => {
      this.set(key, data, ttl);
      this.pendingRequests.delete(key);
      return data;
    }).catch((error) => {
      this.pendingRequests.delete(key);
      throw error;
    });

    this.pendingRequests.set(key, request);
    return request;
  }

  /**
   * Get cache statistics for debugging
   */
  getStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }
}

// Export singleton instance
export const apiCache = new ApiCache();

// Cache key generators for consistency
export const cacheKeys = {
  mundus: (id: string) => `mundus:${id}`,
  munduses: (userId: string) => `munduses:${userId}`,
  dailyPlan: (mundusId: string, date: string) => `dailyPlan:${mundusId}:${date}`,
  messages: (mundusId: string, agentId?: string) => `messages:${mundusId}:${agentId || 'all'}`,
  achievements: (mundusId: string) => `achievements:${mundusId}`,
  profile: (userId: string) => `profile:${userId}`,
};

// Default TTL values (in milliseconds)
export const cacheTTL = {
  short: 30 * 1000,      // 30 seconds
  medium: 2 * 60 * 1000, // 2 minutes
  long: 5 * 60 * 1000,   // 5 minutes
  veryLong: 15 * 60 * 1000, // 15 minutes
};

export default apiCache;
